'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  AcademicCapIcon,
  UsersIcon,
  CurrencyDollarIcon,
  BookOpenIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';


interface SearchResult {
  id: string;
  type: 'student' | 'program' | 'staff' | 'fee';
  title: string;
  subtitle: string;
  href: string;
  meta?: string;
}

interface UnifiedSearchProps {
  role: string;
}

const typeConfig: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  student: { label: 'Student', icon: AcademicCapIcon, color: 'text-blue-500 bg-blue-50' },
  program: { label: 'Program', icon: BookOpenIcon, color: 'text-purple-500 bg-purple-50' },
  staff: { label: 'Staff', icon: UsersIcon, color: 'text-green-500 bg-green-50' },
  fee: { label: 'Fee Record', icon: CurrencyDollarIcon, color: 'text-amber-500 bg-amber-50' },
};

// Which result types each role can see
const roleAccess: Record<string, Array<'student' | 'program' | 'staff' | 'fee'>> = {
  student: ['program'],
  staff: ['student', 'program'],
  coordinator: ['student', 'program', 'staff'],
  executive: ['student', 'program', 'staff', 'fee'],
  sponsor: ['student', 'program'],
  reporting: ['student', 'program', 'staff', 'fee'],
  finance: ['student', 'fee'],
  default: ['student', 'program', 'staff', 'fee'],
};

function getPortalHref(role: string, type: string): string {
  switch (type) {
    case 'student': return role === 'coordinator' ? '/coordinator-portal' : role === 'finance' ? '/finance-portal' : role === 'executive' ? '/executive-dashboard' : '/staff-trainer-portal';
    case 'program': return role === 'coordinator' ? '/coordinator-portal' : role === 'executive' ? '/executive-dashboard' : '/reporting-analytics';
    case 'staff': return role === 'coordinator' ? '/coordinator-portal' : '/reporting-analytics';
    case 'fee': return '/finance-portal';
    default: return '/';
  }
}

export default function UnifiedSearch({ role }: UnifiedSearchProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const supabase = createClient();

  const allowedTypes = roleAccess[role] ?? roleAccess['default'];

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    const found: SearchResult[] = [];
    const term = q.trim().toLowerCase();

    try {
      // Search students
      if (allowedTypes.includes('student')) {
        const { data: students } = await supabase
          .from('students')
          .select('id, full_name, student_number, program_id, enrollment_status')
          .or(`full_name.ilike.%${term}%,student_number.ilike.%${term}%`)
          .limit(5);
        if (students) {
          students.forEach((s) => {
            found.push({
              id: `student-${s.id}`,
              type: 'student',
              title: s.full_name || 'Unknown Student',
              subtitle: `ID: ${s.student_number || 'N/A'}`,
              meta: s.enrollment_status,
              href: getPortalHref(role, 'student'),
            });
          });
        }
      }

      // Search programs
      if (allowedTypes.includes('program')) {
        const { data: programs } = await supabase
          .from('programs')
          .select('id, name, code, is_active')
          .or(`name.ilike.%${term}%,code.ilike.%${term}%`)
          .limit(5);
        if (programs) {
          programs.forEach((p) => {
            found.push({
              id: `program-${p.id}`,
              type: 'program',
              title: p.name || 'Unknown Program',
              subtitle: `Code: ${p.code || 'N/A'}`,
              meta: p.is_active ? 'active' : 'inactive',
              href: getPortalHref(role, 'program'),
            });
          });
        }
      }

      // Search staff
      if (allowedTypes.includes('staff')) {
        const { data: staff } = await supabase
          .from('user_profiles')
          .select('id, full_name, email, role, department')
          .in('role', ['staff', 'coordinator', 'finance'])
          .or(`full_name.ilike.%${term}%,email.ilike.%${term}%,department.ilike.%${term}%`)
          .limit(5);
        if (staff) {
          staff.forEach((s) => {
            found.push({
              id: `staff-${s.id}`,
              type: 'staff',
              title: s.full_name || s.email || 'Unknown Staff',
              subtitle: s.department || s.email || '',
              meta: s.role,
              href: getPortalHref(role, 'staff'),
            });
          });
        }
      }

      // Search fee records
      if (allowedTypes.includes('fee')) {
        const { data: fees } = await supabase
          .from('students')
          .select('id, full_name, student_number, fee_status, fee_outstanding')
          .or(`full_name.ilike.%${term}%,student_number.ilike.%${term}%`)
          .not('fee_status', 'is', null)
          .limit(5);
        if (fees) {
          fees.forEach((f) => {
            found.push({
              id: `fee-${f.id}`,
              type: 'fee',
              title: f.full_name || 'Unknown Student',
              subtitle: `Fee Status: ${f.fee_status || 'N/A'}`,
              meta: f.fee_outstanding ? `PGK ${Number(f.fee_outstanding).toLocaleString()}` : undefined,
              href: getPortalHref(role, 'fee'),
            });
          });
        }
      }
    } catch {
      // silently fail — search is non-critical
    }

    setResults(found);
    setLoading(false);
  }, [allowedTypes, role, supabase]);

  useEffect(() => {
    const timer = setTimeout(() => {
      search(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, search]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const r = results[activeIndex];
      if (r) {
        router.push(r.href);
        setOpen(false);
        setQuery('');
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  }

  function handleClear() {
    setQuery('');
    setResults([]);
    setOpen(false);
    inputRef.current?.focus();
  }

  const showDropdown = open && (query.trim().length >= 2);

  return (
    <div ref={containerRef} className="relative w-full max-w-xs lg:max-w-sm">
      {/* Input */}
      <div className="relative flex items-center">
        <MagnifyingGlassIcon className="absolute left-3 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
          onFocus={() => { if (query.trim().length >= 2) setOpen(true); }}
          onKeyDown={handleKeyDown}
          placeholder="Search students, programs, staff…"
          className="w-full pl-9 pr-8 py-2 text-sm bg-muted border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
        />
        {query && (
          <button
            onClick={handleClear}
            className="absolute right-2.5 p-0.5 rounded text-muted-foreground hover:text-foreground transition-colors"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-card border border-border rounded-xl card-shadow-md z-50 overflow-hidden animate-fade-in">
          {loading ? (
            <div className="px-4 py-6 text-center">
              <div className="inline-block w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-muted-foreground mt-2">Searching…</p>
            </div>
          ) : results.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <UserCircleIcon className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No results for &ldquo;{query}&rdquo;</p>
            </div>
          ) : (
            <div className="max-h-80 overflow-y-auto divide-y divide-border">
              {results.map((result, idx) => {
                const cfg = typeConfig[result.type];
                const Icon = cfg.icon;
                return (
                  <button
                    key={result.id}
                    onClick={() => { router.push(result.href); setOpen(false); setQuery(''); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60 ${idx === activeIndex ? 'bg-muted/60' : ''}`}
                  >
                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${cfg.color}`}>
                      <Icon className="w-4 h-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-500 text-foreground truncate">{result.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-1">
                      <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full ${cfg.color}`}>
                        {cfg.label}
                      </span>
                      {result.meta && (
                        <span className="text-[10px] text-muted-foreground capitalize">{result.meta}</span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <div className="px-4 py-2 border-t border-border bg-muted/30">
            <p className="text-[10px] text-muted-foreground">
              {results.length > 0 ? `${results.length} result${results.length !== 1 ? 's' : ''} · ` : ''}
              Showing results for your role
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
